
import { Module } from '../types';

export const SSR_MODULE: Module = {
  id: "advanced-6",
  title: "Server-Side Rendering (SSR) and Static Site Generation",
  description: "Master SSR concepts, Next.js fundamentals, and static site generation techniques",
  duration: "75 minutes",
  difficulty: "advanced",
  content: `# Server-Side Rendering (SSR) and Static Site Generation

## Introduction to SSR

Server-Side Rendering (SSR) is a technique where React components are rendered on the server instead of the client, providing better performance and SEO benefits.

## Benefits of SSR

### Performance
- Faster initial page load
- Better perceived performance
- Reduced time to first contentful paint

### SEO
- Search engines can crawl pre-rendered content
- Better social media sharing with meta tags
- Improved accessibility

## Next.js Fundamentals

Next.js is the most popular React framework for SSR and static site generation.

\`\`\`jsx
// pages/index.js - Basic Next.js page
import { GetServerSideProps } from 'next';

function HomePage({ data }) {
  return (
    <div>
      <h1>Welcome to my SSR app</h1>
      <p>Data from server: {data.message}</p>
    </div>
  );
}

// This function runs on the server
export const getServerSideProps: GetServerSideProps = async (context) => {
  // Fetch data from an API or database
  const data = { message: "Hello from server!" };
  
  return {
    props: {
      data,
    },
  };
};

export default HomePage;
\`\`\`

## Static Site Generation (SSG)

SSG pre-renders pages at build time, providing the fastest possible loading experience.

\`\`\`jsx
// Using getStaticProps for SSG
export async function getStaticProps() {
  const posts = await fetchPosts();
  
  return {
    props: {
      posts,
    },
    // Regenerate the page at most once every hour
    revalidate: 3600,
  };
}

// For dynamic routes
export async function getStaticPaths() {
  const paths = await getPostPaths();
  
  return {
    paths,
    fallback: 'blocking',
  };
}
\`\`\`

## Hydration Process

Hydration is the process of attaching event listeners to server-rendered HTML.

\`\`\`jsx
// Ensuring components work during hydration
import { useEffect, useState } from 'react';

function ClientOnlyComponent() {
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);
  
  if (!mounted) {
    return <div>Loading...</div>;
  }
  
  return <div>Client-only content</div>;
}
\`\`\`

## Performance Optimization

### Code Splitting
\`\`\`jsx
import dynamic from 'next/dynamic';

const DynamicComponent = dynamic(
  () => import('../components/HeavyComponent'),
  {
    loading: () => <p>Loading...</p>,
    ssr: false, // Disable SSR for this component
  }
);
\`\`\`

### Image Optimization
\`\`\`jsx
import Image from 'next/image';

function OptimizedImage() {
  return (
    <Image
      src="/hero.jpg"
      alt="Hero image"
      width={800}
      height={400}
      priority
      placeholder="blur"
    />
  );
}
\`\`\`

## Best Practices

1. **Use SSG when possible** - Pre-rendered pages are fastest
2. **Implement proper error boundaries** - Handle server-side errors gracefully
3. **Optimize bundle size** - Use code splitting and dynamic imports
4. **Handle hydration mismatches** - Ensure server and client render the same content
5. **Monitor Core Web Vitals** - Track performance metrics`,
  codeExamples: [
    {
      title: "Dynamic SSR with Data Fetching",
      code: `import { GetServerSideProps } from 'next';
import { useState } from 'react';

interface User {
  id: number;
  name: string;
  email: string;
}

interface Props {
  users: User[];
  timestamp: string;
}

function UsersPage({ users, timestamp }: Props) {
  const [filter, setFilter] = useState('');
  
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(filter.toLowerCase())
  );
  
  return (
    <div>
      <h1>Users (Rendered at: {timestamp})</h1>
      <input
        type="text"
        placeholder="Filter users..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      
      <ul>
        {filteredUsers.map(user => (
          <li key={user.id}>
            <strong>{user.name}</strong> - {user.email}
          </li>
        ))}
      </ul>
    </div>
  );
}

export const getServerSideProps: GetServerSideProps<Props> = async () => {
  // Simulate API call
  const users: User[] = [
    { id: 1, name: 'Alice', email: 'alice@example.com' },
    { id: 2, name: 'Bob', email: 'bob@example.com' },
    { id: 3, name: 'Charlie', email: 'charlie@example.com' },
  ];
  
  return {
    props: {
      users,
      timestamp: new Date().toISOString(),
    },
  };
};

export default UsersPage;`,
      explanation: "This example shows how to fetch data on the server and provide it to a React component, while maintaining client-side interactivity.",
      language: "tsx"
    }
  ],
  practice: {
    task: "Create a blog post page that uses SSG with ISR (Incremental Static Regeneration) and includes proper error handling and loading states.",
    starter: `// pages/blog/[slug].tsx
import { GetStaticProps, GetStaticPaths } from 'next';

interface BlogPost {
  slug: string;
  title: string;
  content: string;
  publishedAt: string;
}

interface Props {
  post: BlogPost | null;
}

function BlogPostPage({ post }: Props) {
  // Add your implementation here
  
  return (
    <div>
      {/* Your JSX here */}
    </div>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  // Implement this function
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  // Implement this function
};

export default BlogPostPage;`,
    solution: `// pages/blog/[slug].tsx
import { GetStaticProps, GetStaticPaths } from 'next';
import { useRouter } from 'next/router';
import ErrorPage from 'next/error';

interface BlogPost {
  slug: string;
  title: string;
  content: string;
  publishedAt: string;
}

interface Props {
  post: BlogPost | null;
}

function BlogPostPage({ post }: Props) {
  const router = useRouter();
  
  // Show loading state during ISR
  if (router.isFallback) {
    return <div>Loading...</div>;
  }
  
  // Show error page if post not found
  if (!post) {
    return <ErrorPage statusCode={404} />;
  }
  
  return (
    <article>
      <header>
        <h1>{post.title}</h1>
        <time dateTime={post.publishedAt}>
          {new Date(post.publishedAt).toLocaleDateString()}
        </time>
      </header>
      
      <main>
        <div>{post.content}</div>
      </main>
      
      <footer>
        <button onClick={() => router.back()}>
          ← Back to blog
        </button>
      </footer>
    </article>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  // Pre-generate only the most popular posts
  const popularSlugs = ['getting-started', 'advanced-tips'];
  
  const paths = popularSlugs.map((slug) => ({
    params: { slug },
  }));
  
  return {
    paths,
    fallback: 'blocking', // Generate other pages on-demand
  };
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const slug = params?.slug as string;
  
  try {
    // Simulate fetching from CMS or API
    const post = await fetchBlogPost(slug);
    
    if (!post) {
      return {
        notFound: true,
      };
    }
    
    return {
      props: {
        post,
      },
      revalidate: 60, // Regenerate at most once per minute
    };
  } catch (error) {
    console.error('Error fetching blog post:', error);
    
    return {
      props: {
        post: null,
      },
      revalidate: 10, // Retry sooner on error
    };
  }
};

// Mock API function
async function fetchBlogPost(slug: string): Promise<BlogPost | null> {
  const posts: Record<string, BlogPost> = {
    'getting-started': {
      slug: 'getting-started',
      title: 'Getting Started with SSR',
      content: '<p>Learn the basics of server-side rendering...</p>',
      publishedAt: '2023-01-15T10:00:00Z',
    },
    'advanced-tips': {
      slug: 'advanced-tips',
      title: 'Advanced SSR Tips',
      content: '<p>Take your SSR skills to the next level...</p>',
      publishedAt: '2023-02-20T14:30:00Z',
    },
  };
  
  return posts[slug] || null;
}

export default BlogPostPage;`,
    hints: [
      "Use getStaticPaths with fallback: 'blocking' for dynamic routes",
      "Implement proper error handling with notFound and error states",
      "Add revalidate property for ISR functionality",
      "Handle router.isFallback for loading states",
      "Use ErrorPage component for 404 errors"
    ]
  }
};
